
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import {
  LEAP_CARD_PLACEHOLDER,
  formatLeapCardInput,
  leapCardNumberError
} from '@/helpers/leapCard'
import BasketExpiryWarning from '@/components/BasketExpiryWarning.vue'
import BasketFooter from '@/components/BasketFooter.vue'
import router from '@/router'
import useBasket from '@/composables/useBasket'
import useTravelPass from '@/composables/useTravelPass'

interface LeapCard {
  number: string;
  touched: boolean;
}

export default defineComponent({
  components: {
    BasketExpiryWarning,
    BasketFooter
  },
  setup () {
    const { youngAdultPassengerCount, travelPassesFromBasket } = useTravelPass()
    const { basketId } = useBasket()
    const emptyCards = (count: number): LeapCard[] =>
      Array.from({ length: count }, () => ({ number: '', touched: false }))
    const cards = ref<LeapCard[]>(emptyCards(youngAdultPassengerCount.value))

    // The pass details are held in memory, so refetch them after a refresh
    onMounted(() => {
      if (!youngAdultPassengerCount.value && basketId.value) {
        travelPassesFromBasket(basketId.value)
      }
    })

    watch(youngAdultPassengerCount, count => {
      if (count !== cards.value.length) cards.value = emptyCards(count)
    })

    const updateNumber = (card: LeapCard, event: Event): void => {
      const input = event.target as HTMLInputElement
      const { value, caret } = formatLeapCardInput(
        input.value,
        input.selectionStart ?? input.value.length,
        card.number,
        (event as InputEvent).inputType
      )

      card.number = value
      // Vue skips the DOM update when the formatted value is unchanged, so
      // write it back ourselves before restoring the caret
      input.value = value
      input.setSelectionRange(caret, caret)
    }

    const errorFor = (card: LeapCard): string | null =>
      card.touched ? leapCardNumberError(card.number) : null

    // Front end validation only, the numbers are not sent to the backend
    const valid = computed((): boolean =>
      cards.value.every(card => !leapCardNumberError(card.number))
    )

    const nextStep = () => {
      router.push({ name: 'TravelPass Payment Details' })
    }

    return {
      cards,
      placeholder: LEAP_CARD_PLACEHOLDER,
      errorFor,
      nextStep,
      updateNumber,
      valid
    }
  }
})
